import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { apiService } from '../services/api';
import { Notification } from '../types';
import { usePlatformAskAi } from '../components/PlatformAskAiHost';
import { FeaturesIcons, Icon } from './features/FeaturesIcons';
import FeatureVideoTheater from './features/FeatureVideoTheater';
import { FEATURE_VIDEOS } from './features/featureVideos';
import {
  FeatureImage,
  FEATURE_IMAGES,
  GALLERY_IMAGES,
  WORKSPACE_GROUPS,
  WorkspaceCategory,
  findFeatureImage,
  getWorkspaceImages,
} from './features/featureAssets';
import './PlatformFeatures.css';
import SeoHead from '../components/SeoHead';
import { FEATURES_SEO, ORGANIZATION_JSON_LD, softwareApplicationJsonLd } from '../utils/publicSite';
import { sendBusinessFunnelEvent } from '../analytics';

const AA_HAIRS_DEMO = 'https://booking.aacore.net/aahairs/';
const AA_SNOW_DEMO = 'https://booking.aacore.net/aasnow/';
const BLOG_HOME = 'https://booking.aacore.net/blog/';
const BLOG_GUIDES = [
  {
    label: 'Set up your website',
    href: 'https://booking.aacore.net/blog/set-up-aacore-booking-business-website/',
  },
  {
    label: 'Book with AACore AI',
    href: 'https://booking.aacore.net/blog/book-with-aacore-ai/',
  },
  {
    label: 'Explore the dashboard',
    href: 'https://booking.aacore.net/blog/business-dashboard-guide/',
  },
] as const;

function nailsDemoHref(): string {
  if (process.env.REACT_APP_ENV === 'production') return 'https://booking.aacore.net/aanails/';
  if (process.env.REACT_APP_ENV === 'qa') return 'https://qa.booking.aacore.net/aanails/';
  return '/ddd/booking';
}

type ViewerGroup = FeatureImage[];

const WORKSPACE_TABS: Array<{ id: WorkspaceCategory; icon: string; label: string }> = [
  { id: 'all', icon: 'expand', label: 'All screens' },
  { id: 'website', icon: 'web', label: 'Business website' },
  { id: 'ai', icon: 'spark', label: 'AI booking' },
  { id: 'business', icon: 'calendar', label: 'Business tools' },
];

const TOOL_CARDS = [
  {
    icon: 'calendar',
    title: 'In-store, on-site, and recurring',
    desc: 'Offer appointments that fit how and where you work.',
    bullets: [
      'One-time and recurring appointments',
      'Service addresses and notes for on-site visits',
      'Available time slots, closures, and time off',
    ],
  },
  {
    icon: 'quote',
    title: 'Quote requests and offers',
    desc: 'Agree on the work and price before scheduling the job.',
    bullets: [
      'Customer quote requests',
      'Business review and offer responses',
      'Appointments from accepted offers',
    ],
  },
  {
    icon: 'card',
    title: 'Invoices and card payments',
    desc: 'Keep customer invoices connected to the work.',
    bullets: [
      'Line items, PDF invoices, and payment status',
      'Credit-card invoice payments through Stripe',
      'Payment receipts and notifications',
    ],
  },
  {
    icon: 'user',
    title: 'Customer and appointment history',
    desc: 'Find the details you need without searching across separate tools.',
    bullets: [
      'Customer contact records',
      'Upcoming and past appointments',
      'Quote and invoice history',
    ],
  },
  {
    icon: 'team',
    title: 'Professional and team access',
    desc: 'Coordinate a solo practice or a team of service providers.',
    bullets: [
      'Professional schedules and availability',
      'Role-based business access',
      'Assigned appointments and team views',
    ],
  },
  {
    icon: 'bell',
    title: 'Reminders and notifications',
    desc: 'Keep customers and your team informed as plans change.',
    bullets: [
      'Email confirmations, changes, and reminders',
      'Quote, invoice, deposit, and payment notices',
      'Customer and business notifications',
    ],
  },
];

const PlatformFeatures: React.FC = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: chatOpen, setOpen: setChatOpen } = usePlatformAskAi();
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [workspaceCategory, setWorkspaceCategory] = useState<WorkspaceCategory>('all');
  const [workspacePositions, setWorkspacePositions] = useState<Record<WorkspaceCategory, number>>({
    all: 0,
    website: 0,
    ai: 0,
    business: 0,
  });
  const [viewer, setViewer] = useState<{ open: boolean; index: number; group: ViewerGroup; syncWorkspace: boolean }>({
    open: false,
    index: 0,
    group: GALLERY_IMAGES,
    syncWorkspace: false,
  });
  const imageDialogRef = useRef<HTMLDialogElement>(null);
  const expandTriggerRef = useRef<HTMLButtonElement | null>(null);
  const nailsHref = useMemo(() => nailsDemoHref(), []);

  const workspaceImages = getWorkspaceImages(workspaceCategory);
  const workspaceIndex = workspacePositions[workspaceCategory];
  const workspaceItem = workspaceImages[workspaceIndex] ?? workspaceImages[0];
  const viewerItem = viewer.group[viewer.index];

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((item: Notification) => !item.is_read));
      } catch (error) {
        console.error('Error loading notifications:', error);
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  useEffect(() => {
    sendBusinessFunnelEvent('view_features');
  }, []);

  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollToTarget = () => {
      const header = document.querySelector('nav.sticky');
      const headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 72;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const frame = window.requestAnimationFrame(scrollToTarget);
    const later = window.setTimeout(scrollToTarget, 250);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(later);
    };
  }, [location.hash]);

  useEffect(() => {
    const dialog = imageDialogRef.current;
    if (!dialog) return;
    if (viewer.open && !dialog.open) {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
    if (!viewer.open && dialog.open) {
      dialog.close();
    }
  }, [viewer.open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!viewer.open) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepViewer(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepViewer(1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openViewer = (
    id: string,
    group: ViewerGroup,
    trigger: HTMLButtonElement | null,
    syncWorkspace = false
  ) => {
    const index = Math.max(0, group.findIndex((item) => item.id === id));
    expandTriggerRef.current = trigger;
    setViewer({ open: true, index, group, syncWorkspace });
  };

  const closeViewer = () => {
    setViewer((current) => ({ ...current, open: false }));
    document.body.style.overflow = '';
    expandTriggerRef.current?.focus({ preventScroll: true });
  };

  const stepViewer = (delta: number) => {
    setViewer((current) => {
      if (!current.group.length) return current;
      const nextIndex = (current.index + delta + current.group.length) % current.group.length;
      if (current.syncWorkspace) {
        const nextId = current.group[nextIndex].id;
        setWorkspacePositions((positions) => ({ ...positions, [workspaceCategory]: current.group.findIndex((item) => item.id === nextId) }));
      }
      return { ...current, index: nextIndex };
    });
  };

  const stepWorkspace = (delta: number) => {
    setWorkspacePositions((positions) => {
      const count = workspaceImages.length;
      return { ...positions, [workspaceCategory]: (positions[workspaceCategory] + delta + count) % count };
    });
  };

  const scrollToVideos = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById('video-showcase')?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const screenRatio = workspaceItem && workspaceItem.width && workspaceItem.height
    ? workspaceItem.width / workspaceItem.height
    : 1.6;

  return (
    <div className="min-h-screen flex flex-col bg-[#f3f6f9]">
      <SeoHead
        title={FEATURES_SEO.title}
        description={FEATURES_SEO.description}
        canonicalPath={FEATURES_SEO.path}
        jsonLd={[ORGANIZATION_JSON_LD, softwareApplicationJsonLd()]}
      />
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        showAuthButtons={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <div className="features-v7 flex-1">
        <FeaturesIcons />
        <main id="main" className="container">
          <Link className="back" to="/">
            <Icon name="left" />
            Back to AACore Booking
          </Link>

          <section className="hero hero-v2" aria-labelledby="hero-title">
            <div className="hero-copy">
              <p className="hero-kicker"><Icon name="spark" /> Built around your service business</p>
              <h1 id="hero-title">Online booking software built for service businesses</h1>
              <p>A place to be found. A smarter way to book. Your business website, AI-assisted booking, and the tools to manage what happens next. All in AACore Booking.</p>
              <p>Customers can book directly, request on-site appointments, set up recurring visits, or start with a quote. You can send invoices and collect payments in the same account.</p>
              <div className="hero-actions">
                <Link className="button primary" to="/business-registration/">
                  <Icon name="web" />
                  Create a business account
                </Link>
                <button className="button hero-ask" type="button" aria-controls="aac-chat" aria-expanded={chatOpen} onClick={() => setChatOpen(true)}>
                  <Icon name="chat" />
                  Ask about AACore
                </button>
              </div>
              <a className="hero-demo" href="#demos"><Icon name="play" />Explore a live business demo</a>
            </div>
            <div className="hero-product">
              <div className="product-heading">
                <span><Icon name="web" />Your business, ready to book</span>
                <span className="product-tag">Actual product</span>
              </div>
              <button
                className="hero-screen screen-frame"
                type="button"
                aria-label="Expand the public business website"
                onClick={(event) => openViewer('website', GALLERY_IMAGES, event.currentTarget)}
              >
                <img src={findFeatureImage('website')?.src} alt="The real AA Nails business website, with booking, business details, and photos." />
                <span className="expand" aria-hidden="true"><Icon name="expand" /></span>
              </button>
              <div className="product-caption">
                <Icon name="spark" />
                <span>Customers chat. AI prepares the booking.<br /><strong>Your customer confirms.</strong></span>
                <button type="button" aria-label="Ask how AI booking works" aria-controls="aac-chat" aria-expanded={chatOpen} onClick={() => setChatOpen(true)}>
                  <Icon name="chat" />
                </button>
              </div>
            </div>
          </section>

          <ol className="journey-strip" aria-label="The connected customer journey">
            <li><span className="journey-icon"><Icon name="web" /></span><div><strong>Get discovered</strong><span>Your public business website</span></div></li>
            <li><span className="journey-icon"><Icon name="spark" /></span><div><strong>Get booked</strong><span>AI chat or online booking</span></div></li>
            <li><span className="journey-icon"><Icon name="calendar" /></span><div><strong>Stay organized</strong><span>Customers, schedules, and quotes</span></div></li>
            <li><span className="journey-icon"><Icon name="card" /></span><div><strong>Get paid</strong><span>Invoices and Stripe payments</span></div></li>
          </ol>

          <nav className="jump-nav" aria-label="Features on this page">
            <a href="#ai-booking"><Icon name="spark" />AI booking</a>
            <a href="#video-showcase"><Icon name="play" />Videos</a>
            <a href="#website"><Icon name="web" />Business website</a>
            <a href="#business-tools"><Icon name="calendar" />Business tools</a>
            <a href="#screenshots"><Icon name="expand" />Screenshots</a>
            <a href="#guides"><Icon name="quote" />Guides</a>
            <a href="#coming-soon"><Icon name="mic" />Coming soon</a>
          </nav>

          <section id="ai-booking" className="ai-feature" aria-labelledby="ai-title">
            <div className="feature-copy">
              <div className="eyebrow"><Icon name="spark" />Meet AACore AI</div>
              <h2 id="ai-title">Book through a conversation.</h2>
              <p>Customers tell the AI assistant what they need. It helps them choose services and an available appointment, then asks them to review and confirm.</p>
              <ul className="check-list">
                <li><Icon name="check" />Chat to book an appointment</li>
                <li><Icon name="check" />Review the details before confirming</li>
                <li><Icon name="check" />Manage appointments through chat</li>
              </ul>
              <div className="feature-actions">
                <a className="button primary" href={nailsHref} target={nailsHref.startsWith('http') ? '_blank' : undefined} rel={nailsHref.startsWith('http') ? 'noopener noreferrer' : undefined}>Try Demo Store</a>
                <button className="button" type="button" onClick={scrollToVideos}>
                  <Icon name="play" />
                  Watch feature videos
                </button>
              </div>
              <p className="status-note"><Icon name="mic" />Voice booking and management are coming soon.</p>
            </div>
            <div>
              <div className="ai-shots">
                <figure className="shot">
                  <button className="shot-button screen-frame" type="button" aria-label="Expand choosing services in AI chat" onClick={(event) => openViewer('ai-choose', GALLERY_IMAGES, event.currentTarget)}>
                    <img src={findFeatureImage('ai-choose')?.src} alt="Real Ask AACore AI conversation selecting services, a professional and a date." width={460} height={720} />
                    <span className="expand" aria-hidden="true"><Icon name="expand" /></span>
                  </button>
                  <figcaption>Choose your services</figcaption>
                </figure>
                <figure className="shot">
                  <button className="shot-button screen-frame" type="button" aria-label="Expand AI booking review" onClick={(event) => openViewer('ai-review', GALLERY_IMAGES, event.currentTarget)}>
                    <img src={findFeatureImage('ai-review')?.src} alt="Real AI booking summary with services, time, price, and Confirm and Book." width={460} height={720} />
                    <span className="expand" aria-hidden="true"><Icon name="expand" /></span>
                  </button>
                  <figcaption>Review and confirm</figcaption>
                </figure>
              </div>
              <p className="demo-note">Select either screen to view it larger.</p>
            </div>
          </section>

          <section id="video-showcase" className="feature-section video-showcase" aria-labelledby="video-showcase-title">
            <div className="video-heading">
              <div>
                <h2 id="video-showcase-title">See AACore Booking in motion.</h2>
                <p>Choose one story at a time: the platform overview, a real salon example, a founder-led business invitation, or a complete quote workflow.</p>
              </div>
              <span className="video-note">{FEATURE_VIDEOS.length} selected videos</span>
            </div>
            <FeatureVideoTheater />
          </section>

          <section id="website" className="feature-section website-feature" aria-labelledby="website-title">
            <div className="feature-copy">
              <div className="eyebrow"><Icon name="web" />Your online presence</div>
              <h2 id="website-title">A free business website customers can book from.</h2>
              <p>Show your services, photos, hours, and contact details on a public business page. Share one link on social media, in messages, or on your Google business profile.</p>
              <ul className="check-list">
                <li><Icon name="check" />Your services, prices, and booking entry point</li>
                <li><Icon name="check" />Business branding, photos, hours, and policies</li>
                <li><Icon name="check" />Customers can search by keyword and category</li>
              </ul>
              <div className="url-example">booking.aacore.net/<span>your-business</span></div>
            </div>
            <div className="hairs-preview">
              <div className="hairs-shots">
                {FEATURE_IMAGES.map((image) => (
                  <figure className="shot" key={image.id}>
                    <button
                      className="shot-button screen-frame"
                      type="button"
                      aria-label={image.id === 'aahairs-gallery' ? 'Expand the AA Hairs photo gallery' : 'Expand the AA Hairs service list'}
                      onClick={(event) => openViewer(image.id, FEATURE_IMAGES, event.currentTarget)}
                    >
                      <img src={image.src} alt={image.alt} width={image.width} height={image.height} loading="lazy" />
                      <span className="expand" aria-hidden="true"><Icon name="expand" /></span>
                    </button>
                    <figcaption>{image.id === 'aahairs-gallery' ? 'Showcase your work' : 'Explore your services'}</figcaption>
                  </figure>
                ))}
              </div>
              <p className="demo-note">Select either screen to view it larger.</p>
            </div>
          </section>

          <section id="screenshots" className="feature-section workspace-section" aria-labelledby="workspace-title">
            <span id="owner-tools" className="explorer-anchor" aria-hidden="true" />
            <div className="workspace-heading">
              <div>
                <h2 id="workspace-title">See how it comes together.</h2>
                <p>Explore your website, AI booking, and business tools.</p>
              </div>
              <Icon name="web" />
            </div>
            <div className="workspace-tabs" role="group" aria-label="Choose a screenshot category">
              {WORKSPACE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  aria-pressed={workspaceCategory === tab.id}
                  aria-controls="workspace-carousel"
                  onClick={() => setWorkspaceCategory(tab.id)}
                >
                  <Icon name={tab.icon} />
                  <span>{tab.label} <span>({WORKSPACE_GROUPS[tab.id].images.length})</span></span>
                </button>
              ))}
            </div>
            <div className="workspace-view">
              <div
                className="workspace-carousel"
                id="workspace-carousel"
                role="region"
                aria-roledescription="carousel"
                aria-label={`${WORKSPACE_GROUPS[workspaceCategory].label} image viewer`}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    stepWorkspace(event.key === 'ArrowLeft' ? -1 : 1);
                  }
                }}
              >
                <div className="workspace-stage">
                  <button
                    className="workspace-image screen-frame"
                    type="button"
                    aria-label="Expand selected screenshot"
                    style={{ ['--screen-ratio' as string]: String(screenRatio) }}
                    onClick={(event) => openViewer(workspaceItem.id, workspaceImages, event.currentTarget, true)}
                  >
                    <img
                      src={workspaceItem.src}
                      alt={workspaceItem.alt}
                      width={workspaceItem.width}
                      height={workspaceItem.height}
                    />
                    <span className="expand" aria-hidden="true"><Icon name="expand" /></span>
                  </button>
                  <button
                    className="workspace-arrow workspace-prev"
                    type="button"
                    aria-label="Previous image in selected category"
                    hidden={workspaceImages.length < 2}
                    onClick={() => stepWorkspace(-1)}
                  >
                    <Icon name="left" />
                  </button>
                  <button
                    className="workspace-arrow workspace-next"
                    type="button"
                    aria-label="Next image in selected category"
                    hidden={workspaceImages.length < 2}
                    onClick={() => stepWorkspace(1)}
                  >
                    <Icon name="right" />
                  </button>
                </div>
                <p className="workspace-position" aria-live="polite" aria-atomic="true">
                  <span>{workspaceIndex + 1} / {workspaceImages.length}</span>
                  <span>{workspaceItem.title}</span>
                </p>
              </div>
              <div className="workspace-summary">
                <h3>{workspaceItem.title}</h3>
                <p>{workspaceItem.caption}</p>
                <button type="button" className="button" aria-controls="aac-chat" aria-expanded={chatOpen} onClick={() => setChatOpen(true)}>
                  <Icon name="chat" />
                  Ask about AACore
                </button>
                <div className="workspace-evidence"><Icon name="check" />Actual product screens</div>
              </div>
            </div>
          </section>

          <section id="business-tools" className="feature-section" aria-labelledby="tools-title">
            <div className="section-heading">
              <div>
                <h2 id="tools-title">The tools behind every appointment.</h2>
                <p>Keep the customer journey connected, from the first request to the final payment.</p>
              </div>
            </div>
            <div className="tools-grid">
              {TOOL_CARDS.map((card) => (
                <article className="tool-card" key={card.title}>
                  <Icon name={card.icon} />
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                  <ul>
                    {card.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                </article>
              ))}
            </div>
            <div className="wide-band">
              <h3><Icon name="web" />Your services, your setup</h3>
              <p>Manage service categories, descriptions, durations, pricing, and booking policies from your business account.</p>
            </div>
            <p className="detail">Online payments require a connected Stripe account. Payment processing fees may apply.</p>
          </section>

          <section id="demos" className="feature-section" aria-labelledby="demos-title">
            <div className="section-heading">
              <div>
                <h2 id="demos-title">Try the customer experience.</h2>
                <p>Explore a demo business before setting up your own. Sign in as a customer to continue into booking.</p>
              </div>
            </div>
            <div className="demos">
              <a className="demo-card" href={nailsHref} target={nailsHref.startsWith('http') ? '_blank' : undefined} rel={nailsHref.startsWith('http') ? 'noopener noreferrer' : undefined}>
                <span className="demo-mark"><Icon name="spark" /></span>
                <div><h3>AA Nails</h3><p>In-store services and AI booking</p></div>
                <Icon name="arrow" />
              </a>
              <a className="demo-card" href={AA_HAIRS_DEMO} target="_blank" rel="noopener noreferrer">
                <span className="demo-mark"><Icon name="team" /></span>
                <div><h3>AA Hairs</h3><p>Hair services and staff scheduling</p></div>
                <Icon name="arrow" />
              </a>
              <a className="demo-card" href={AA_SNOW_DEMO} target="_blank" rel="noopener noreferrer">
                <span className="demo-mark"><Icon name="location" /></span>
                <div><h3>AA Snow</h3><p>On-site service appointments</p></div>
                <Icon name="arrow" />
              </a>
            </div>
          </section>

          <section id="guides" className="guides-band" aria-labelledby="guides-title">
            <div className="guides-band-top">
              <div>
                <h2 id="guides-title">See how it works, step by step.</h2>
                <p>Explore practical articles and video guides for your business website, AACore AI, bookings, quotes, and payments. Follow the instructions at your own pace.</p>
              </div>
              <a className="button primary" href={BLOG_HOME}>Browse the Blog</a>
            </div>
            <div className="guides-links">
              {BLOG_GUIDES.map((guide) => (
                <a key={guide.href} href={guide.href}>{guide.label}</a>
              ))}
            </div>
          </section>

          <div className="platform-end">
            <section className="panel" aria-labelledby="industries-title">
              <h2 id="industries-title">Built for service businesses.</h2>
              <p>Start with your services and availability, whether you work alone, run a team, or travel to customers.</p>
              <div className="industry-list">
                <span>Salons & wellness</span>
                <span>Home services</span>
                <span>Cleaning</span>
                <span>Childcare</span>
                <span>Clinics &amp; specialty services</span>
                <span>Service teams</span>
              </div>
            </section>
            <section id="coming-soon" className="panel" aria-labelledby="upcoming-title">
              <h2 id="upcoming-title">What’s coming next</h2>
              <div className="coming-list">
                <div className="coming-item">
                  <Icon name="mic" />
                  <div>
                    <strong>Voice booking and management</strong>
                    <p>Use voice to book and manage appointments.</p>
                    <span>Coming soon</span>
                  </div>
                </div>
                <div className="coming-item">
                  <Icon name="gift" />
                  <div>
                    <strong>Gift Cards</strong>
                    <p>Sell and redeem gift cards for appointments and services.</p>
                    <span>Coming soon</span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <section className="security" aria-labelledby="security-title">
            <h2 id="security-title">Platform and payment protections</h2>
            <div className="security-grid">
              <div>
                <h3><Icon name="web" />US-based cloud hosting</h3>
                <p>Hosted on AWS cloud infrastructure in the United States.</p>
              </div>
              <div>
                <h3><Icon name="shield" />Protected data</h3>
                <p>Data is encrypted in transit and at rest.</p>
              </div>
              <div>
                <h3><Icon name="card" />Stripe payment processing</h3>
                <p>Card payments are handled through Stripe. AACore Booking does not store customer card numbers.</p>
              </div>
            </div>
          </section>

          <section className="final-cta" aria-labelledby="signup-title">
            <div>
              <h2 id="signup-title">Bring your services and bookings together.</h2>
              <p>Create a business account and start preparing your public booking page.</p>
            </div>
            <Link className="button" to="/business-registration/">
              Create a business account
              <Icon name="arrow" />
            </Link>
          </section>
          <p className="customer-cta">Looking to book a service? <Link to="/register?type=customer">Create a customer account</Link>.</p>
        </main>

        <dialog
          ref={imageDialogRef}
          className="image-dialog"
          aria-labelledby="dialog-title"
          onClose={closeViewer}
        >
          {viewerItem && (
            <>
              <div className="dialog-header">
                <h2 id="dialog-title">{viewerItem.title}</h2>
                <button className="icon-button" type="button" aria-label="Close expanded screenshot" onClick={closeViewer}>
                  <Icon name="close" />
                </button>
              </div>
              <div className="dialog-image-area">
                <img src={viewerItem.src} alt={viewerItem.alt} />
              </div>
              <div className="dialog-footer">
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Previous screenshot"
                  hidden={viewer.group.length < 2}
                  onClick={() => stepViewer(-1)}
                >
                  <Icon name="left" />
                </button>
                <p className="dialog-caption">
                  <span>{viewer.index + 1} / {viewer.group.length}</span>
                  <strong>{viewerItem.caption}</strong>
                </p>
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Next screenshot"
                  hidden={viewer.group.length < 2}
                  onClick={() => stepViewer(1)}
                >
                  <Icon name="right" />
                </button>
              </div>
              <p className="sr-only" aria-live="polite">
                {viewer.index + 1} of {viewer.group.length}: {viewerItem.title}
              </p>
            </>
          )}
        </dialog>

      </div>

      <Footer />
    </div>
  );
};

export default PlatformFeatures;
